/**
 * Helper utilities for managing multi-file upload state
 */

import { FileState, FileUploads } from '../stateSegments/fileUploadSegment';

/**
 * Creates initial file state for a new file upload
 */
export const createInitialFileState = (
  fileId: string,
  fileName: string,
  fileSize?: number
): FileState => ({
  fileId,
  fileName,
  fileSize,
  status: 'uploading',
  uploadProgress: 0,
  extractionProgress: 0,
  retryCount: 0,
  isDeleting: false,
});

/**
 * Updates a specific file's state in the files Map
 */
export const updateFileState = (
  currentState: FileUploads,
  fileId: string,
  updates: Partial<FileState>
): FileUploads => {
  const files = new Map(currentState.files);
  const existingFile = files.get(fileId);

  if (existingFile) {
    files.set(fileId, { ...existingFile, ...updates });
  }

  return {
    ...currentState,
    files,
  };
};

/**
 * Adds a new file to the state
 */
export const addFileToState = (
  currentState: FileUploads,
  fileState: FileState
): FileUploads => {
  const files = new Map(currentState.files);
  files.set(fileState.fileId, fileState);

  return {
    ...currentState,
    files,
  };
};

/**
 * Removes a file from the state
 */
export const removeFileFromState = (
  currentState: FileUploads,
  fileId: string
): FileUploads => {
  const files = new Map(currentState.files);
  files.delete(fileId);

  return {
    ...currentState,
    files,
  };
};

/**
 * Gets all active file IDs (uploading, extracting, or analyzing)
 */
export const getActiveFileIds = (files: Map<string, FileState>): string[] => {
  return Array.from(files.values())
    .filter(file =>
      ['uploading', 'extracting', 'analyzing'].includes(file.status)
    )
    .map(file => file.fileId);
};

/**
 * Checks if all files have completed (success or error)
 */
export const areAllFilesComplete = (files: Map<string, FileState>): boolean => {
  if (files.size === 0) return true;

  return Array.from(files.values()).every(
    file => file.status === 'completed' || file.status === 'error'
  );
};

/**
 * Gets count of files by status
 */
export const getFileStatusCounts = (files: Map<string, FileState>) => {
  const counts = {
    total: files.size,
    uploading: 0,
    extracting: 0,
    analyzing: 0,
    completed: 0,
    error: 0,
  };

  Array.from(files.values()).forEach(file => {
    switch (file.status) {
      case 'uploading':
        counts.uploading++;
        break;
      case 'extracting':
        counts.extracting++;
        break;
      case 'analyzing':
        counts.analyzing++;
        break;
      case 'completed':
        counts.completed++;
        break;
      case 'error':
        counts.error++;
        break;
    }
  });

  return counts;
};

/**
 * Gets file names for the selected file IDs
 * @param files - Map of all files
 * @param selectedFileIds - Array of selected file IDs
 * @returns Array of file names for the selected files
 */
export const getSelectedFileNames = (
  files: Map<string, FileState>,
  selectedFileIds: string[]
): string[] => {
  return selectedFileIds
    .map(fileId => {
      const file = files.get(fileId);
      return file?.customFileName || file?.fileName;
    })
    .filter((name): name is string => name !== undefined);
};

/**
 * Gets comma-separated file names for selected files
 * @param files - Map of all files
 * @param selectedFileIds - Array of selected file IDs
 * @returns Comma-separated string of file names
 */
export const getSelectedFileNamesAsString = (
  files: Map<string, FileState>,
  selectedFileIds: string[]
): string => {
  const fileNames = getSelectedFileNames(files, selectedFileIds);
  return fileNames.join(', ');
};

/**
 * Gets file type from fileName extension
 */
const getFileType = (fileName: string): string => {
  const lowerFileName = fileName.toLowerCase();
  if (lowerFileName.endsWith('.pdf')) return 'pdf';
  if (lowerFileName.endsWith('.docx')) return 'docx';
  if (lowerFileName.endsWith('.doc')) return 'doc';
  return 'pdf'; // Default to pdf
};

/**
 * Formats file size to human-readable format
 * @param size - Either bytes (number) or already formatted string from API
 */
const formatFileSize = (size?: number | string): string => {
  if (!size) return '';
  // If already formatted string from API (e.g., "23.01 MB"), return as-is
  if (typeof size === 'string') return size;
  // Format number (bytes) to KB or MB
  const kb = size / 1024;
  if (kb < 1024) return `${kb.toFixed(2)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(2)} MB`;
};

/**
 * Gets selected files with their fileId, fileName, fileSize, fileType, and brand
 * @param files - Map of all files
 * @param selectedFileIds - Array of selected file IDs
 * @returns Array of objects containing fileId, fileName, fileSize, fileType, and brand for selected files
 */
export const getSelectedFilesList = (
  files: Map<string, FileState>,
  selectedFileIds: string[]
): Array<{
  fileId: string;
  fileName: string;
  customFileName?: string;
  fileSize?: string;
  fileType?: string;
  brand?: string;
}> => {
  return selectedFileIds
    .map(fileId => {
      const file = files.get(fileId);
      if (file) {
        const fileName = file.fileName;
        return {
          fileId: file.fileId,
          fileName: fileName,
          customFileName: file.customFileName,
          fileSize: formatFileSize(file.fileSize),
          fileType: getFileType(fileName),
          brand: file.extractedMetadata?.brand,
        };
      }
      return null;
    })
    .filter((file): file is NonNullable<typeof file> => file !== null);
};
